import { Button, CIActionGuard, CIStatusCapsule, Loader, MyIcon, useToastMessage, useUserPermission } from 'ccs-ui-components';
import { UseAuthState } from 'ccs-ui-components/dist/components/ciAuthGuard/store/interfaces';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Dropdown } from 'react-bootstrap';
import { requiredAuthPermission } from '../../../common/constants/UserPermissionConstants';
import { TranslateHook } from '../../modules/journey-canvas/utility/TypeDefs';

import { RESPONSE_CODE } from '../../../common/constants/StatusConstants';
import useConfirmDialog from '../../../common/hooks/ConfirmationDialog';
import useDeleteEntity from '../../../common/hooks/useDeleteEntity';
import { journeyBuilderRoutes } from '../../constants/JdRoutes';
import { Labels } from '../../modules/journey-list/Utility';

import classes from './JourneyBuilderHeader.module.scss';

/**
@method getMoreButtons
@description Generates an array of JSX elements representing more buttons based on the provided parameters.
@param {Object} options - The options object.
@param {boolean} options.showSchedulerBtn - Whether to show the scheduler button.
@param {Object} options.permissionObject - The permission object.
@param {string} options.journeyStatusLower - The lowercase journey status.
@param {Function} options.onSchedulerClick - The function to be called when the scheduler button is clicked.
@param {boolean} options.viewJourneyInitiated - Whether the journey view is initiated.
@param {Function} options.translate - The translate function.
@param {boolean} options.enableSendForApproveBtn - Whether to enable the send for approval button.
@param {Function} options.onApprove - The function to be called when the approve button is clicked.
@param {Function} options.onReturn - The function to be called when the return button is clicked.
@param {Function} options.onSendForApproval - The function to be called when the send for approval button is clicked.
@param {Function} options.commentBtnHandler - The function to be called when the comment button is clicked.
@param {Function} options.onMoveToDraft - The function to be called when the move to draft button is clicked.
@returns {React.JSX.Element[]} - An array of JSX elements representing more buttons.
*/
const getMoreButtons = ({
  showSchedulerBtn,
  permissionObject,
  journeyStatusLower,
  onSchedulerClick,
  viewJourneyInitiated,
  translate,
  enableSendForApproveBtn,
  onApprove,
  onReturn,
  onSendForApproval,
  commentBtnHandler,
  onMoveToDraft,
  deleteActiveEntity,
  handleCloneJourney
}: {
  showSchedulerBtn: boolean,
  permissionObject: UseAuthState,
  journeyStatusLower: string,
  enableSendForApproveBtn: boolean,
  viewJourneyInitiated: boolean,
  translate: TranslateHook,
  onSchedulerClick?: () => void,
  onApprove?: () => void,
  onReturn?: () => void,
  onSendForApproval?: () => void,
  commentBtnHandler?: () => void,
  onMoveToDraft?: () => void,
  deleteActiveEntity: () => void,
  handleCloneJourney: () => void,
}): React.JSX.Element[] => {
  const moreButtons: React.JSX.Element[] = [];

  if (showSchedulerBtn && CIActionGuard([requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve], permissionObject.permission)) {
    moreButtons.push(
      <Dropdown.Item
        disabled={journeyStatusLower !== 'active' || !viewJourneyInitiated}
        onClick={onSchedulerClick}
        data-testid="scheduleBtn"
        key="scheduleBtn"
      >
        {translate('journey_builder_header_component.txt_create_job_schedule')}
      </Dropdown.Item>
    );
  }
  if (journeyStatusLower === Labels.JOURNEY_STATUS_ACTIVE) {
    moreButtons.push(
      <Dropdown.Item
        onClick={deleteActiveEntity}
        data-testid="deleteJourneyButton"
        key="deleteJourneyButton"
        disabled={journeyStatusLower !== Labels.JOURNEY_STATUS_ACTIVE}
      >
        {translate('inactive_records.mark_inactive')}
      </Dropdown.Item>
    );
  }
  if (journeyStatusLower === Labels.JOURNEY_STATUS_RETURNED) {
    moreButtons.push(
      <Dropdown.Item
        onClick={commentBtnHandler}
        data-testid="commentJourneyButton"
        key="commentJourneyButton"
      >
        {translate('journey_builder_header_component.txt_comment')}
      </Dropdown.Item>
    );
  }
  if (CIActionGuard([requiredAuthPermission.JD.approve], permissionObject.permission) && journeyStatusLower === Labels.JOURNEY_STATUS_PENDING_APPROVAL) {
    moreButtons.push(
      <Dropdown.Item
        onClick={onApprove}
        data-testid="approveJourneyButton"
        key="approveJourneyButton"
      >
        {translate('journey_builder_header_component.btn_approve')}
      </Dropdown.Item>
    );
  }
  if (CIActionGuard([requiredAuthPermission.JD.approve], permissionObject.permission) && journeyStatusLower === Labels.JOURNEY_STATUS_PENDING_APPROVAL) {
    moreButtons.push(
      <Dropdown.Item
        onClick={onMoveToDraft}
        data-testid="draftJourneyButton"
        key="draftJourneyButton"
      >
        {translate('journey_builder_header_component.btn_draft')}
      </Dropdown.Item>
    );
  }
  if (CIActionGuard([requiredAuthPermission.JD.approve], permissionObject.permission) && journeyStatusLower === 'pending_approval') {
    moreButtons.push(
      <Dropdown.Item
        onClick={onReturn}
        data-testid="returnJourneyButton"
        key="returnJourneyButton"
      >
        {translate('journey_builder_header_component.btn_return')}
      </Dropdown.Item>
    );
  }
  if (CIActionGuard([requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve], permissionObject.permission) && (!viewJourneyInitiated || (viewJourneyInitiated && journeyStatusLower === 'draft'))) {
    moreButtons.push(
      <Dropdown.Item
        disabled={!enableSendForApproveBtn}
        onClick={onSendForApproval}
        data-testid="sendForApprovalJourneyButton"
        key="sendForApprovalJourneyButton"
      >
        {translate('journey_builder_header_component.btn_send_for_approval')}
      </Dropdown.Item>
    );
  }
  moreButtons.push(
    <Dropdown.Item
      data-testid="cloneBtn"
      key="cloneBtn"
      onClick={handleCloneJourney}
      disabled={journeyStatusLower === 'inactive' || !viewJourneyInitiated}
    >
      {translate('inactive_records.clone_record')}
    </Dropdown.Item>
  );

  return moreButtons;
};

const JourneyBuilderHeader: React.FC<{
  journeyStepName: string,
  journeyName: string,
  journeyStatus: string,
  journeyVersion: number,
  saveBtnText: string,
  enableSaveBtn?: boolean,
  enableSendForApproveBtn?: boolean,
  showSchedulerBtn?: boolean,
  disableAllBtn?: boolean,
  viewJourneyInitiated?: boolean,
  onSave?: () => void,
  onCancel?: (leave: boolean) => void,
  onSchedulerClick?: () => void,
  onEdit?: () => void,
  onApprove?: () => void,
  onReturn?: () => void,
  onSendForApproval?: () => void,
  onFullscreen?: () => void,
  commentBtnHandler?: () => void,
  onMoveToDraft?: () => void,
  onDeleteSuccess?: () => void,
  journeyDescription?: string,
}> = (props) => {
  const {
    journeyStepName,
    journeyName,
    journeyStatus,
    journeyVersion,
    enableSaveBtn = false,
    enableSendForApproveBtn = false,
    onSave,
    onCancel,
    onSchedulerClick,
    showSchedulerBtn = false,
    disableAllBtn = false,
    onEdit,
    onApprove,
    onReturn,
    onSendForApproval,
    saveBtnText,
    viewJourneyInitiated = false,
    onFullscreen,
    commentBtnHandler,
    onMoveToDraft,
    onDeleteSuccess,
    journeyDescription,
  } = props;

  // hooks
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  const history = useNavigate();

  const { deleteEntity } = useDeleteEntity();
  const [isDeleting, setIsDeleting] = useState(false);
  const { renderToast, success, failure } = useToastMessage();
  const { show: showDialog, render: renderDialog } = useConfirmDialog();

  // Use translation as fallback if journeyDescription is not provided
  const displayJourneyDescription = journeyDescription || String(translate('journey_builder_component.msg_no_description_provided'));

  /**
   * @method deleteActiveEntity
   * @description deletes the active entity
   */
  const deleteActiveEntity = async () => {
    showDialog({
      title: translate('inactive_records.msg_mark_inactive_header'),
      message: translate('inactive_records.msg_mark_inactive'),
      cancelText: translate('confirm-dialog-prompt-box.lbl_close'),
      okText: translate('inactive_records.confirm_mark_inactive'),
      testId: 'confirm_dialog'
    }).then(async (confirm) => {
      if (confirm) {
        // call delete API
        try {
          setIsDeleting(true);
          const { statusCode, deleteResponse, errorMessage } = await deleteEntity(`journeys/${journeyName}/version/${journeyVersion}`);
          if (statusCode === RESPONSE_CODE.DELETE_SUCCESS) {
            success(
              translate('inactive_records.success_title'),
              translate('inactive_records.msg_delete_success')
            );
            if (onDeleteSuccess) onDeleteSuccess();
          } else {
            const error = deleteResponse?.errorMessage ?? errorMessage;
            failure(
              translate('inactive_records.lbl_delete_fail'),
              `${error}`
            );
          }
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  /**
   * @method handleCloneJourney
   * @description redirects to create journey by passing name and version from state
   */
  const handleCloneJourney = () => {
    showDialog({ message: translate('inactive_records.clone_message'), title: `${translate('inactive_records.clone_record')}-${journeyName}`, cancelText: translate('confirm-dialog-prompt-box.lbl_close'), okText: translate('confirm-dialog-prompt-box.lbl_proceed') }).then((confirmStatus) => {
      if (confirmStatus) {
        history(journeyBuilderRoutes.createJourney, { state: { cloneJourneyName: journeyName, cloneJourneyVersion: journeyVersion } });
      }
    });
  };

  // local variables
  const journeyStatusLower = journeyStatus.toLowerCase();

  const moreButtons = getMoreButtons({
    enableSendForApproveBtn,
    journeyStatusLower,
    permissionObject,
    showSchedulerBtn,
    translate,
    commentBtnHandler,
    onApprove,
    onMoveToDraft,
    onReturn,
    onSchedulerClick,
    onSendForApproval,
    viewJourneyInitiated,
    deleteActiveEntity,
    handleCloneJourney
  });

  return (
    <nav className={classes.navbar}>
      <Loader show={isDeleting} />
      {renderDialog()}
      {renderToast()}
      <div className={classes.name}>
        <Button variant="text" onClick={onCancel?.bind(null, true)} data-testid="journeyListButton">{translate('journey_list_component.title_journeys')}</Button>
        <MyIcon className={classes.muted} name="rightArrowIcon" />
        {!journeyStepName && <h1 className={classes.muted} title={`${journeyName}\n${displayJourneyDescription}`}>{journeyName}</h1>}
        {journeyStepName && (
          <>
            <Button variant="text" onClick={onCancel?.bind(null, false)} title={`${journeyName}\n${displayJourneyDescription}`} data-testid="journeyName">{journeyName}</Button>
            <MyIcon className={classes.muted} name="rightArrowIcon" />
            <h1 className={classes.muted} title={journeyStepName} data-testid="journeyStepName">{journeyStepName}</h1>
          </>
        )}
        {!!journeyStatus && <CIStatusCapsule testId="journeyStatus" status={journeyStatus} className={classes['status-alignment']} />}
      </div>
      <div className={`${classes.buttons} ci-button-group`}>
        {!!journeyVersion && <span>{translate('journey_builder_header_component.txt_version')} {journeyVersion}</span>}
        {!!journeyStatus && (
          <Button
            className={classes['btn-cancel']}
            variant="secondary"
            onClick={onCancel?.bind(null, false)}
            data-testid="cancelJourneyButton"
            disabled={disableAllBtn}
          >
            {translate('journey_builder_header_component.btn_cancel')}
          </Button>
        )}
        {(viewJourneyInitiated && CIActionGuard([requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve], permissionObject.permission) && !!journeyStatus && journeyStatusLower !== Labels.JOURNEY_STATUS_PENDING_APPROVAL) && (
          <Button
            variant="primary"
            onClick={onEdit}
            data-testid="editJourneyButton"
            disabled={disableAllBtn}
          >
            {translate('journey_builder_header_component.btn_edit')}
          </Button>
        )}
        {CIActionGuard([requiredAuthPermission.JD.approve, requiredAuthPermission.JD.edit], permissionObject.permission) && !viewJourneyInitiated && !!journeyStatus && (
          <Button
            variant="primary"
            onClick={onSave}
            data-testid="saveJourneyButton"
            disabled={disableAllBtn || !enableSaveBtn}
          >
            {saveBtnText}
          </Button>
        )}
        <Button variant="text" className={classes['btn-more']} type="button" onClick={onFullscreen} title={document.fullscreenElement ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
          {document.fullscreenElement ? (
            <i className="fa-solid fa-compress" />
          ) : (
            <i className="fa-solid fa-expand" />
          )}
        </Button>
        <Dropdown>
          <Dropdown.Toggle
            title="More"
            style={{
              visibility: moreButtons.length > 0 ? 'visible' : 'hidden'
            }}
            className={classes['btn-more']}
            variant="text"
            disabled={disableAllBtn || moreButtons.length === 0}
            data-testid="dropdownBtn"
          >
            <i className="bi bi-three-dots-vertical" />
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {moreButtons}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </nav>
  );
};
export default JourneyBuilderHeader;
